// Henry-Griffitts Training Modules Data
// 36 modules with slides and quizzes
//
// SUPERSEDED 2026-08-19. This is the course that ran BEFORE HG Education, and it is no longer
// reachable in the app -- see LEGACY_MODULES_VOIDED in App.js.
//
// KEPT, NOT DELETED, deliberately. Randall's ruling was hide-don't-delete, and this file is
// the only place the 36 modules ever existed: no database collection was ever created for
// them (verified against production 2026-08-19 -- no `modules`, `slide_videos` or
// `module_progress` collection, and 0 users with any `completed_modules`). Deleting the file
// would be the one action that actually destroyed the content.

export const MODULES = [
  {
    id: 1,
    category: "Company Overview",
    title: "Henry-Griffitts Values",
    slides: [
      {
        title: "Our Core Philosophy",
        content:
          "Henry-Griffitts was built on one unshakeable belief: every golfer deserves equipment that is fitted precisely to their unique swing. We do not sell off-the-rack golf clubs. We fit. We customize. We deliver.",
        image: null,
      },
      {
        title: "The Three Pillars",
        content:
          "**Precision** — Every club is built to exacting tolerances. **Integrity** — We tell golfers the truth about their swing, not what they want to hear. **Results** — Our fitters see real improvement in every customer they fit correctly.",
        image: null,
      },
      {
        title: "Why This Matters to You",
        content:
          "As an authorized Henry-Griffitts fitter, you represent this standard. Your reputation is built on the same values. Customers who are fitted correctly become customers for life — and they send their friends.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What is the core belief Henry-Griffitts was built on?",
        options: [
          "Every golfer should buy premium clubs",
          "Every golfer deserves precisely fitted equipment",
          "Custom clubs are only for professionals",
          "Fitting is secondary to club quality",
        ],
        answer: 1,
      },
      {
        q: "Which of the following is NOT one of the Three Pillars?",
        options: ["Precision", "Integrity", "Speed", "Results"],
        answer: 2,
      },
    ],
  },
  {
    id: 2,
    category: "Company Overview",
    title: "Introduction",
    slides: [
      {
        title: "Welcome to Henry-Griffitts Certification",
        content:
          "This training program is designed to make you one of the most knowledgeable club fitters in the industry. By the end of this course, you will understand not just what specifications to fit — but WHY each specification matters to the golfer's swing.",
        image: null,
      },
      {
        title: "What You Will Learn",
        content:
          "You will master: Lie angle theory and measurement using the HG Lie Board. Club length determination via squareness of hit. Shaft fitting, loft, head design, weight and swingweight. The complete Henry-Griffitts Clubfitting Model. How to sell fittings and follow up with customers.",
        image: null,
      },
      {
        title: "How This Course Works",
        content:
          "Each module builds on the last. Complete the slides, then pass the quiz with 80% or higher to unlock the next module. You can retake quizzes as many times as needed. Once all modules are complete, you will receive your Henry-Griffitts Certification.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What score do you need to pass each module quiz?",
        options: ["70%", "75%", "80%", "90%"],
        answer: 2,
      },
      {
        q: "What do you receive when all modules are complete?",
        options: [
          "A discount on clubs",
          "Henry-Griffitts Certification",
          "Access to pricing",
          "A free fitting kit",
        ],
        answer: 1,
      },
    ],
  },
  {
    id: 3,
    category: "Company Overview",
    title: "The Company's History",
    slides: [
      {
        title: "Founded on a Fitting Philosophy",
        content:
          "Henry-Griffitts was founded with the conviction that the standard golf industry model — manufacture clubs in standard specs and sell them to all golfers — was fundamentally flawed. Most golfers are using clubs that do not fit them, and they blame themselves for their poor ball-striking.",
        image: null,
      },
      {
        title: "The ICS System",
        content:
          "Henry-Griffitts developed the ICS (Individual Clubfitting System) — a proprietary method for measuring a golfer's natural swing tendencies and matching clubs precisely to those tendencies. The lie board is the cornerstone of this system.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What does ICS stand for?",
        options: [
          "International Club Standard",
          "Individual Clubfitting System",
          "Integrated Custom Specs",
          "Iron Calibration System",
        ],
        answer: 1,
      },
    ],
  },
  {
    id: 4,
    category: "Club Fitting Fundamentals",
    title: "Balance & Evaluation",
    slides: [
      {
        title: "Why Evaluate Before You Fit",
        content:
          "Every fitting begins with observation. Before you measure anything, watch the golfer swing. Notice their natural tendencies — how they stand, how they move through the ball, where they make contact. This tells you more than any static measurement.",
        image: null,
      },
      {
        title: "Static vs. Dynamic Evaluation",
        content:
          "**Static measurements** (wrist-to-floor, height, hand size) give you a starting point. **Dynamic evaluation** — watching the golfer actually swing — tells you the truth. A golfer's address position and swing plane will often contradict what static measurements suggest.",
        image: null,
      },
      {
        title: "The Address Position",
        content:
          "Observe the golfer from down the line and face-on. Note: spine angle, knee flex, distance from the ball, arm hang. A golfer who stands too upright will need flatter lie angles. A golfer who is very bent over will need more upright angles.",
        image: null,
      },
      {
        title: "Assessing Swing Tempo",
        content:
          "Tempo directly impacts shaft selection. A golfer with a fast, aggressive tempo creates more shaft load and typically needs stiffer shafts. A smooth, rhythmic swinger can play softer flex effectively — and often benefits greatly from it.",
        image: null,
      },
      {
        title: "The Wrist-to-Floor Measurement",
        content:
          "Have the golfer stand in their natural, relaxed posture — shoes on, feet shoulder-width apart. Measure from the floor to the crease of the wrist. This measurement feeds directly into the club length starting point on the HG Fit Chart.",
        image: null,
      },
      {
        title: "Hand Size and Grip Sizing",
        content:
          "Measure the golfer's hand from the base of the palm to the tip of the middle finger. This determines starting grip size. Then confirm with a grip test — proper grip should allow the ring finger to lightly touch the palm without digging in.",
        image: null,
      },
      {
        title: "Clubhead Speed Estimation",
        content:
          "You do not need a launch monitor for every fitting. Observe the golfer's build, tempo, and strength. A general guide: Under 70 mph = Ladies/Senior flex. 70–85 mph = Regular flex. 85–95 mph = Stiff. Over 95 mph = X-Stiff.",
        image: null,
      },
      {
        title: "The Fitting Conversation",
        content:
          "Ask every golfer: What do your miss-hits do? Where does the ball typically go? What do you wish your clubs did better? These answers tell you exactly what to look for on the lie board and during shaft testing.",
        image: null,
      },
      {
        title: "Recording Your Observations",
        content:
          "Use the Henry-Griffitts Fitting Form to record everything. Do not rely on memory. Document the wrist-to-floor, hand size, estimated swing speed, tempo observation, and any comments about miss patterns before you proceed to lie board testing.",
        image: null,
      },
      {
        title: "Setting Expectations",
        content:
          "Help the golfer understand that the fitting process takes 45–60 minutes when done properly. Rushed fittings produce poor results and unhappy customers. A golfer who understands and respects the process will value the outcome far more.",
        image: null,
      },
      {
        title: "Summary: The Evaluation Sequence",
        content:
          "1. Observe the golfer at address. 2. Measure wrist-to-floor. 3. Measure hand size. 4. Estimate swing speed and tempo. 5. Conduct the fitting conversation. 6. Record everything on the Fitting Form. Now you are ready for the Lie Board.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "Which type of evaluation reveals the most about how a golfer actually plays?",
        options: [
          "Static measurements",
          "Dynamic evaluation",
          "Wrist-to-floor only",
          "Hand size measurement",
        ],
        answer: 1,
      },
      {
        q: "What swing speed range typically corresponds to Regular flex?",
        options: ["Under 70 mph", "70–85 mph", "85–95 mph", "Over 95 mph"],
        answer: 1,
      },
      {
        q: "How long should a proper fitting take?",
        options: [
          "15–20 minutes",
          "20–30 minutes",
          "45–60 minutes",
          "Over 90 minutes",
        ],
        answer: 2,
      },
    ],
  },
  {
    id: 5,
    category: "Club Fitting Fundamentals",
    title: "Fit Chart",
    slides: [
      {
        title: "The Henry-Griffitts Fit Chart",
        content:
          "The HG Fit Chart is your master reference document. It cross-references wrist-to-floor measurements, height, and swing tendencies to provide a starting point for club length and lie angle. It is a starting point — the lie board provides the final answer.",
        image: null,
      },
      {
        title: "Reading the Fit Chart",
        content:
          "Find the golfer's wrist-to-floor measurement on the left axis, cross-reference with their height. The chart will indicate whether standard length, +1/4\", +1/2\", -1/4\" or -1/2\" is appropriate as a baseline. Remember: squareness of hit on the lie board will confirm or adjust this.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "The Fit Chart provides a _____ for club length — what confirms the final specification?",
        options: [
          "Final answer",
          "Starting point — the lie board confirms",
          "Guarantee",
          "Maximum range",
        ],
        answer: 1,
      },
    ],
  },
  {
    id: 6,
    category: "Lie Angle",
    title: "Lie Angle Summary",
    slides: [
      {
        title: "What is Lie Angle?",
        content:
          "Lie angle is the angle between the center of the shaft and the ground line when the club is soled properly at address. It is one of the most important — and most commonly incorrect — specifications on a set of golf clubs.",
        image: null,
      },
      {
        title: "Why Lie Angle Matters",
        content:
          "When a club's lie angle is wrong, the face cannot be square to the target at impact regardless of how good the swing is. A club that is too upright will push the toe up and close the face — causing pulls. Too flat raises the heel and opens the face — causing pushes.",
        image: null,
      },
      {
        title: "The Industry Problem",
        content:
          "Over 80% of golfers play clubs with lie angles that do not match their swing. Most manufacturers build to a 'standard' that is designed for a 5'10\" male golfer with average proportions. Most golfers don't fit that profile.",
        image: null,
      },
      {
        title: "The HG Solution",
        content:
          "Henry-Griffitts builds clubs to the lie angle that is correct for each individual golfer as determined by lie board testing. This is not an upcharge or an option — it is the standard. Every set is fitted.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What happens when a club is too upright at impact?",
        options: [
          "The face opens, causing pushes",
          "The face closes, causing pulls",
          "The ball goes straight",
          "Distance is lost only",
        ],
        answer: 1,
      },
      {
        q: "Approximately what percentage of golfers play clubs with incorrect lie angles?",
        options: ["20%", "50%", "Over 80%", "100%"],
        answer: 2,
      },
    ],
  },
  {
    id: 7,
    category: "Lie Angle",
    title: "Lie Angle, The Theory",
    slides: [
      {
        title: "The Physics of Lie Angle",
        content:
          "At impact, the club travels on an arc. The lowest point of that arc — where the club bottoms out — is where lie angle matters most. If the toe is up at that moment, gear effect and face angle both conspire to push the ball left. If the heel is up, the ball goes right.",
        image: null,
      },
      {
        title: "Lie Angle and Swing Arc",
        content:
          "Every golfer has a unique swing arc determined by their height, arm length, posture, and swing mechanics. The lie angle must match the angle at which the club naturally approaches the ground through their swing — not a theoretical ideal.",
        image: null,
      },
      {
        title: "Dynamic vs. Static Lie Angle",
        content:
          "**Static lie** is measured with the club at rest. **Dynamic lie** is the actual angle at impact during the swing. These are often different. The shaft bends under load, the golfer's posture changes, and the club path shifts. The lie board measures dynamic lie.",
        image: null,
      },
      {
        title: "The 1-Degree Rule",
        content:
          "For every 1 degree of lie angle error, the ball is deflected approximately 3–4 yards at 150 yards distance. A golfer with a 4-degree lie error will miss their target by 12–16 yards consistently — and will never understand why.",
        image: null,
      },
      {
        title: "Lie Angle Across the Set",
        content:
          "Lie angle is not constant across a set. As clubs get shorter (higher irons), the golfer stands closer to the ball and more upright. Shorter clubs need progressively more upright lie angles. Standard sets are built with a 1-degree progression — HG customizes this to each golfer.",
        image: null,
      },
      {
        title: "Compensations for Wrong Lie",
        content:
          "When golfers play incorrect lie angles for years, they develop compensating swing moves. They loop the club, manipulate their wrists, or adjust their stance to make the ball go straight. These compensations make the swing inconsistent and exhausting.",
        image: null,
      },
      {
        title: "Correcting Lie Angle Mid-Set",
        content:
          "It is possible for a golfer to need different lie angle corrections at different points in the set. Their 5-iron lie may be correct while their 9-iron needs adjustment. The lie board reveals this by testing each iron individually.",
        image: null,
      },
      {
        title: "Temperature and Shaft Effects",
        content:
          "Steel shafts bend less dynamically than graphite. A golfer playing graphite-shafted irons may need slightly different lie angles than the same golfer playing steel. Always test with the shaft material the golfer will actually play.",
        image: null,
      },
      {
        title: "Summary",
        content:
          "Lie angle is the single most important fitting specification for iron play. It affects direction, consistency, and forces compensations that corrupt the natural swing. The HG lie board is the most accurate method available to measure and correct it.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "For every 1 degree of lie angle error, how many yards off-target is a 150-yard shot?",
        options: ["1–2 yards", "3–4 yards", "5–6 yards", "7–8 yards"],
        answer: 1,
      },
      {
        q: "What does the lie board measure?",
        options: ["Static lie angle", "Dynamic lie angle", "Club length", "Swing speed"],
        answer: 1,
      },
    ],
  },
  {
    id: 8,
    category: "Lie Angle",
    title: "The HG Lie Board",
    slides: [
      {
        title: "What is the Lie Board?",
        content:
          "The Henry-Griffitts Lie Board is a flat board with a piece of impact tape placed on the sole of the club. When the golfer hits a ball off the board, the tape marks where the sole contacted the board — revealing the true dynamic lie angle at impact.",
        image: null,
      },
      {
        title: "Setting Up the Lie Board",
        content:
          "Place the lie board on a flat, solid surface. It must not rock or flex. Position the golf ball on the small tee or rubber tee supplied with the board — the ball must sit at normal height, not elevated. The board must be perpendicular to the target line.",
        image: null,
      },
      {
        title: "Applying the Impact Tape",
        content:
          "Apply a fresh strip of impact tape to the sole of the iron being tested. The tape should run heel-to-toe across the center of the sole. Do not overlap or bunch the tape — a smooth, single layer gives the clearest mark.",
        image: null,
      },
      {
        title: "The Test Swing",
        content:
          "Ask the golfer to make a normal swing — not a special or careful swing. You want to see their natural motion, not an attempt to hit the board perfectly. Typically 3–5 swings with a mid-iron (6 or 7 iron) establishes a reliable pattern.",
        image: null,
      },
      {
        title: "Reading the Primary Mark",
        content:
          "After each swing, examine the mark left on the tape. The position of the mark (heel, center, or toe) and the angle of the mark tell you exactly what the dynamic lie angle is doing at impact. This is the core skill of lie board interpretation.",
        image: null,
      },
      {
        title: "The Center Mark",
        content:
          "A centered mark — running straight down the middle of the sole — indicates correct lie angle. The golfer's swing is delivering the club at the angle it was designed for. No lie angle correction is needed.",
        image: null,
      },
      {
        title: "Consistency Is Key",
        content:
          "One mark is not a fitting. Look for the consistent pattern across 3–5 swings. A consistent toe mark means the club is consistently too flat. A consistent heel mark means it is too upright. Inconsistent marks indicate a swing path issue to address separately.",
        image: null,
      },
      {
        title: "Using Multiple Clubs",
        content:
          "Test at minimum a 5-iron, 7-iron, and 9-iron. Because golfers stand progressively closer and more upright for shorter clubs, the lie angle needs may differ across the set. This is normal and expected. Document each club separately.",
        image: null,
      },
      {
        title: "Board Maintenance",
        content:
          "Keep the lie board clean and flat. Replace the board surface if it shows grooves or indentations from repeated use — these can affect mark accuracy. Store the board flat, not leaning against a wall which can cause warping.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "How many swings should you take to establish a reliable lie angle pattern?",
        options: ["1 swing", "2 swings", "3–5 swings", "10+ swings"],
        answer: 2,
      },
      {
        q: "Which clubs should you test at minimum?",
        options: ["7-iron only", "5, 7, and 9-iron", "All 9 irons", "3-iron and 9-iron"],
        answer: 1,
      },
    ],
  },
  {
    id: 9,
    category: "Lie Angle",
    title: "Interpreting Lie Board Results",
    slides: [
      {
        title: "The Mark Language",
        content:
          "The lie board mark is a language. Once you learn to read it, every golfer's swing tells you exactly what it needs. The three primary elements are: mark position (heel to toe), mark angle (straight vs. angled), and mark depth (light vs. heavy).",
        image: null,
      },
      {
        title: "Toe Mark: Too Flat",
        content:
          "When the mark is on the toe side of center, the toe is digging into the board at impact. This means the club is too flat for this golfer. The heel is riding up, opening the face. Correction: increase lie angle (make more upright).",
        image: null,
      },
      {
        title: "Heel Mark: Too Upright",
        content:
          "When the mark is on the heel side of center, the heel is contacting the board. The club is too upright. The toe is riding up, closing the face. Correction: decrease lie angle (make flatter).",
        image: null,
      },
      {
        title: "How Much to Adjust",
        content:
          "As a general guideline: a mark 1/4\" from center = approximately 1 degree of correction needed. A mark 1/2\" from center = 2 degrees. A mark at the extreme heel or toe = 3–4 degrees. Always retest after adjusting the fitting clubs.",
        image: null,
      },
      {
        title: "The Angled Mark",
        content:
          "An angled mark — where one end is deeper than the other — indicates the club is twisting through impact. This is often caused by the golfer's swing path interacting with an incorrect lie angle. It can also indicate a shaft flex issue.",
        image: null,
      },
      {
        title: "Light vs. Heavy Marks",
        content:
          "A very light mark indicates the golfer is not making full contact through the ball. A very heavy mark suggests the golfer is driving the club down aggressively. Neither invalidates the reading — but note the mark depth as context when selecting shaft.",
        image: null,
      },
      {
        title: "The Divot Direction",
        content:
          "Combine the lie board reading with observation of the divot. The divot direction tells you about swing path; the lie board tells you about face angle. Together they give the complete picture of what corrections will genuinely help the golfer.",
        image: null,
      },
      {
        title: "Documenting Results",
        content:
          "On the fitting form, record each club tested, the mark description (e.g., '1/4 toe, straight'), and the recommended lie correction. A completed set might show: 5-iron flat 2°, 7-iron flat 1°, 9-iron flat 1°. This becomes the build specification.",
        image: null,
      },
      {
        title: "Sharing the Results with the Golfer",
        content:
          "Show the golfer their marks. Most have never seen lie board results before and are fascinated. Explain what it means in plain language: 'Your clubs are too flat for your swing, which is why you consistently miss right.' This builds tremendous trust.",
        image: null,
      },
      {
        title: "Retesting After Correction",
        content:
          "When possible, adjust the fitting irons to the recommended lie angle and retest. Seeing the mark move to center — and watching the golfer's shots straighten out — is the most powerful sales tool available. The club literally proves itself.",
        image: null,
      },
      {
        title: "Combining with Length",
        content:
          "Lie angle and length are interconnected. If you change club length, you must recheck lie angle — length changes affect how the golfer stands to the ball, which changes the dynamic lie. Always confirm lie after determining length.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "A mark 1/2\" from center indicates approximately how many degrees of correction?",
        options: ["1 degree", "2 degrees", "3 degrees", "4 degrees"],
        answer: 1,
      },
      {
        q: "What does a toe mark indicate about the club?",
        options: [
          "Too upright — decrease lie angle",
          "Too flat — increase lie angle (more upright)",
          "Correct lie angle",
          "Wrong shaft flex",
        ],
        answer: 1,
      },
    ],
  },
  {
    id: 10,
    category: "Lie Angle",
    title: "Lie Board Marks Reference",
    slides: [
      {
        title: "Mark Type 1: Perfect Center",
        content:
          "Mark runs straight down the center of the sole, equal depth heel and toe. **Reading: Correct lie angle.** No adjustment needed. Document as 'Standard' or 'Spec' on the fitting form.",
        image: null,
      },
      {
        title: "Mark Type 2: Slight Toe",
        content:
          "Mark is centered but slightly toward the toe — approximately 1/8\" off center. **Reading: 1/2 degree flat.** Minor adjustment. May be within tolerance for some golfers, especially if ball flight is consistent.",
        image: null,
      },
      {
        title: "Mark Type 3: Clear Toe",
        content:
          "Mark is clearly in the toe half of the sole — approximately 1/4\" from center. **Reading: 1 degree flat.** Correction needed. Golfer will tend to push or fade shots.",
        image: null,
      },
      {
        title: "Mark Type 4: Strong Toe",
        content:
          "Mark is well into the toe — approximately 3/8\" from center. **Reading: 1.5–2 degrees flat.** Significant correction needed. Most standard sets are this wrong for shorter players.",
        image: null,
      },
      {
        title: "Mark Type 5: Extreme Toe",
        content:
          "Mark is at or near the toe edge. **Reading: 2–3 degrees flat or more.** Golfer is fighting their equipment constantly. Every straight shot requires a compensation.",
        image: null,
      },
      {
        title: "Mark Type 6: Slight Heel",
        content:
          "Mark is centered but slightly toward the heel — approximately 1/8\" off center. **Reading: 1/2 degree upright.** Minor adjustment, often seen in taller golfers or those who stand very close to the ball.",
        image: null,
      },
      {
        title: "Mark Type 7: Clear Heel",
        content:
          "Mark is clearly in the heel half of the sole — approximately 1/4\" from center. **Reading: 1 degree upright.** Correction needed. Golfer will tend to pull or draw shots excessively.",
        image: null,
      },
      {
        title: "Mark Type 8: Strong Heel",
        content:
          "Mark is well into the heel — approximately 3/8\" from center. **Reading: 1.5–2 degrees upright.** Significant correction. Often seen in taller players using standard-length clubs.",
        image: null,
      },
      {
        title: "Mark Type 9: Angled — Toe Deep",
        content:
          "Mark runs diagonally with the toe end deeper than the heel. **Reading: Club is twisting through impact, toe-down.** Indicates too flat, but also potential shaft flex issue — shaft may be too stiff, resisting release.",
        image: null,
      },
      {
        title: "Mark Type 10: Angled — Heel Deep",
        content:
          "Mark runs diagonally with the heel end deeper. **Reading: Club twisting, heel-down.** Indicates too upright, but also consider shaft too soft — shaft over-releasing through impact.",
        image: null,
      },
      {
        title: "Mark Type 11: Very Light Across Center",
        content:
          "Mark is centered but very faint, barely visible. **Reading: Golfer is sweeping, not compressing.** Shaft may be too stiff or golfer is decelerating. Recheck swing tempo and shaft recommendation.",
        image: null,
      },
      {
        title: "Mark Type 12: Heavy Center",
        content:
          "Mark is centered with deep, broad impression. **Reading: Correct lie, aggressive downward attack angle.** Good ball-striker. Consider shaft weight and flex to handle the load.",
        image: null,
      },
      {
        title: "Mark Type 13: Inconsistent — Alternating",
        content:
          "Marks alternate between toe and heel across multiple swings. **Reading: Inconsistent swing path.** Do not fit lie angle from inconsistent marks. Address swing fundamentals first, then retest.",
        image: null,
      },
      {
        title: "Mark Type 14: Consistent Pattern Despite Inconsistent Shots",
        content:
          "Marks are consistent but ball flight varies. **Reading: Lie angle is consistent but another variable (swing path, face angle at address) is causing shot variation.** Fix lie first, then investigate other causes.",
        image: null,
      },
      {
        title: "Mark Type 15: No Mark",
        content:
          "No impression on the tape after a swing. **Causes:** Golfer topped the ball. Ball was teed too high. Tape was not applied properly. Board was not contacted. Reapply tape and try again — do not record a 'no mark' as data.",
        image: null,
      },
      {
        title: "Mark Type 16: Full Sole Contact",
        content:
          "Entire sole width leaves an impression. **Reading: Club is extremely flat — sole leading edge is dragging.** Club may need 3+ degrees upright, or golfer has an extremely flat swing plane. Verify with multiple swings.",
        image: null,
      },
      {
        title: "Mark Type 17: Toe Only — Heel Never Touches",
        content:
          "Only the toe edge is contacting the board — the heel appears to never touch. **Reading: 3+ degrees flat.** Extreme mismatch. This golfer is fighting every shot. Major correction will produce dramatic improvement.",
        image: null,
      },
      {
        title: "Mark Type 18: Heel Only — Toe Never Touches",
        content:
          "Only the heel edge contacts the board. **Reading: 3+ degrees upright.** Often seen when a very tall player is using standard-length clubs that are too short, forcing them to stand very close.",
        image: null,
      },
      {
        title: "Mark Type 19: Progressive Pattern Across Set",
        content:
          "5-iron shows correct lie, 7-iron shows slight toe, 9-iron shows clear toe. **Reading: Lie angle needs are progressive.** Build the set with increasing lie angle correction in the shorter irons. This is a proper set makeup — not one flat correction applied to all clubs.",
        image: null,
      },
      {
        title: "Mark Type 20: Different Patterns by Club Type",
        content:
          "Irons show one pattern; fairway woods show another. **Reading: Golfer's setup or swing changes between clubs.** Fit each club category separately. Do not assume iron lie angles apply to woods.",
        image: null,
      },
      {
        title: "Mark Type 21: After-Correction Retest — Center",
        content:
          "After adjusting to recommended lie angle, the retest shows a perfect center mark. **Reading: Correction was accurate.** This is the goal. Show the golfer. Document the confirmed lie angle as the build specification.",
        image: null,
      },
      {
        title: "Mark Type 22: After-Correction Retest — Still Off",
        content:
          "After adjusting lie angle, mark is still not center. **Reading: Initial correction was insufficient, or golfer's swing changed.** Adjust further and retest. Sometimes two or three iterations are needed for very flat or very upright golfers.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "A mark 3/8\" into the toe indicates what reading?",
        options: ["1 degree flat", "1.5–2 degrees flat", "Correct lie angle", "1 degree upright"],
        answer: 1,
      },
      {
        q: "Marks that alternate between toe and heel across multiple swings indicate:",
        options: [
          "Perfect lie angle",
          "2 degrees flat",
          "Inconsistent swing path — do not fit from these marks",
          "Shaft too stiff",
        ],
        answer: 2,
      },
    ],
  },
  {
    id: 11,
    category: "Club Length",
    title: "Club Length: Squareness of Hit",
    slides: [
      {
        title: "The Real Determinant of Club Length",
        content:
          "Most fitters use wrist-to-floor measurement to set club length. Henry-Griffitts uses a better method: squareness of hit. The correct club length is the length at which the golfer consistently hits the center of the face.",
        image: null,
      },
      {
        title: "Why Wrist-to-Floor Is Incomplete",
        content:
          "Two golfers with identical wrist-to-floor measurements can have completely different swing arcs due to posture, arm position, and swing mechanics. The wrist-to-floor gives a starting point — impact location on the face reveals the truth.",
        image: null,
      },
      {
        title: "Using Impact Tape on the Face",
        content:
          "Apply impact tape to the face of the club being tested. Have the golfer hit 5–7 shots with their normal swing. The cluster of marks reveals where they consistently strike the face.",
        image: null,
      },
      {
        title: "Reading Face Impact Patterns",
        content:
          "**Center cluster:** Length is correct. **Toe cluster:** Club may be too short — golfer reaching. **Heel cluster:** Club may be too long — golfer cramped. **High on face:** Swing is bottoming out early — check posture and setup.",
        image: null,
      },
      {
        title: "Adjusting Based on Face Impact",
        content:
          "If the golfer consistently hits the toe, try a club 1/4\" longer and retest. If consistently hitting the heel, try 1/4\" shorter. Make incremental changes and retest until the cluster centers on the face.",
        image: null,
      },
      {
        title: "The Length-Lie Connection",
        content:
          "Changing club length changes how the golfer stands to the ball, which changes their swing arc, which changes the dynamic lie angle. Always recheck lie angle after finalizing club length. They cannot be set independently.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "According to Henry-Griffitts, what is the real determinant of correct club length?",
        options: [
          "Wrist-to-floor measurement",
          "Height",
          "Squareness of hit on the face",
          "Arm length",
        ],
        answer: 2,
      },
      {
        q: "A consistent toe impact pattern suggests:",
        options: [
          "Club is too long",
          "Club may be too short — golfer is reaching",
          "Correct length",
          "Wrong lie angle only",
        ],
        answer: 1,
      },
    ],
  },
  {
    id: 12,
    category: "Shaft Fitting",
    title: "Shafts",
    slides: [
      {
        title: "Why Shafts Matter",
        content:
          "The shaft is the engine of the golf club. The wrong shaft will ruin a perfect fitting on every other specification. The right shaft amplifies the golfer's natural swing into a consistent, efficient delivery of the clubhead.",
        image: null,
      },
      {
        title: "Shaft Flex",
        content:
          "Flex is the shaft's resistance to bending under swing load. **L (Ladies):** Under 70 mph. **A/Senior:** 70–80 mph. **R (Regular):** 80–90 mph. **S (Stiff):** 90–100 mph. **X (Extra Stiff):** 100+ mph. These are starting points — tempo matters equally.",
        image: null,
      },
      {
        title: "Tempo vs. Swing Speed",
        content:
          "A golfer with an 88 mph swing and a fast, aggressive tempo may need S flex. The same speed with a smooth, rhythmic tempo plays better in R flex. Always evaluate tempo alongside swing speed — they are equally important in shaft selection.",
        image: null,
      },
      {
        title: "Shaft Weight",
        content:
          "Shaft weight affects swing feel and consistency. **Lighter shafts (50–65g):** Easier to swing, helps increase speed for slower swingers. **Mid-weight (70–85g):** Most players. **Heavy (90g+):** Strong, fast players who need control. Always consider the total club weight.",
        image: null,
      },
      {
        title: "Shaft Kick Point",
        content:
          "Kick point (also called bend point) affects trajectory. **High kick point:** Lower ball flight, more boring trajectory — good for strong players who fight high ballooning shots. **Low kick point:** Higher launch, more carry — helps slower swingers get the ball airborne.",
        image: null,
      },
      {
        title: "Steel vs. Graphite for Irons",
        content:
          "Steel is more consistent, provides better feedback, and is preferred by better players and faster swingers. Graphite is lighter, reduces vibration, and benefits seniors, women, and anyone with joint issues. Many good players are switching to lighter graphite — do not automatically assume steel.",
        image: null,
      },
      {
        title: "The Shaft Fitting Process",
        content:
          "1. Establish swing speed. 2. Observe tempo. 3. Ask about trajectory preferences. 4. Test with fitting shafts if available. 5. Cross-reference with the HG shaft recommendation guide. 6. Document selected shaft on fitting form.",
        image: null,
      },
      {
        title: "Graphite for Woods",
        content:
          "Nearly all modern drivers and fairway woods use graphite shafts. For woods, weight is the primary variable — lighter shafts allow faster swing speed. Most golfers benefit from a driver shaft in the 50–65g range.",
        image: null,
      },
      {
        title: "The Impact of Wrong Shaft on Swing",
        content:
          "A shaft too stiff: golfer cannot load it properly, loses distance, tends to push or cut. A shaft too soft: over-loading causes timing issues, hooks, inconsistency. Both create compensations that corrupt the natural swing over time.",
        image: null,
      },
      {
        title: "Shaft and Lie Angle Interaction",
        content:
          "As noted in the lie board section, shaft flex affects how the club delivers at impact. A very soft shaft may show a different lie board mark than a stiffer shaft for the same golfer. Ideally, lie angle is tested with the shaft the golfer will actually play.",
        image: null,
      },
      {
        title: "SST Pure — Coming Up Next",
        content:
          "Henry-Griffitts recommends SST Puring for shafts — a process that identifies and aligns the shaft's most stable plane. This eliminates manufacturing inconsistencies and produces the most consistent shaft in every club of the set.",
        image: null,
      },
      {
        title: "Summary: Shaft Selection Checklist",
        content:
          "Check swing speed measured or estimated. Check tempo observed. Check trajectory preference noted. Check shaft weight appropriate for strength. Check kick point appropriate for launch needs. Check material (steel or graphite) confirmed. Document on fitting form.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What two factors are EQUALLY important in shaft flex selection?",
        options: [
          "Swing speed and club length",
          "Swing speed and tempo",
          "Height and arm length",
          "Grip size and hand size",
        ],
        answer: 1,
      },
      {
        q: "What does a high kick point shaft produce?",
        options: ["Higher ball flight", "Lower ball flight", "More spin", "Longer clubs"],
        answer: 1,
      },
    ],
  },
  {
    id: 13,
    category: "Shaft Fitting",
    title: "Shaft Designations",
    slides: [
      {
        title: "Reading Shaft Codes",
        content:
          "Every shaft has a designation code that tells you flex, weight, and sometimes kick point. Learning to read these codes allows you to quickly identify and compare shafts from different manufacturers.",
        image: null,
      },
      {
        title: "Common Steel Shaft Designations",
        content:
          "**True Temper Dynamic Gold:** S300 = Stiff, 300 series (130g). R300 = Regular, 300 series. X100 = Extra Stiff, 100 series (heavier). **Project X:** 5.5 = Regular. 6.0 = Stiff. 6.5 = X-Stiff.",
        image: null,
      },
      {
        title: "Common Graphite Shaft Designations",
        content:
          "**Aldila:** NV Series with weight (55g, 65g, 75g). **Fujikura:** Speeder, Ventus with flex letter (R, S, X). **Mitsubishi:** Tensei with color codes and flex. Weight is usually in the name (e.g., '60' = 60 grams).",
        image: null,
      },
      {
        title: "Henry-Griffitts Shaft Offerings",
        content:
          "HG offers shafts specifically chosen to match the performance needs of fitted clubs. Consult the current HG catalog for the shaft options available. Most common: True Temper steel in various weights, premium graphite options for seniors and women.",
        image: null,
      },
      {
        title: "Specifying Shafts on the Order Form",
        content:
          "When completing the fitting form, write the full shaft designation — not just 'stiff' or 'regular'. Example: 'True Temper Dynamic Gold S300' or 'Fujikura Ventus Blue 6S'. This prevents errors in the build shop.",
        image: null,
      },
      {
        title: "Special Requests and Alternatives",
        content:
          "Occasionally a golfer will request a shaft not in the standard HG offering. Contact HG directly to discuss special-order options. In most cases, a comparable shaft from the standard lineup will perform equally well.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What does 'S300' indicate in True Temper Dynamic Gold?",
        options: ["Senior flex, 300 series", "Stiff flex, 300 series weight class", "Soft flex", "Standard weight"],
        answer: 1,
      },
    ],
  },
  {
    id: 14,
    category: "Shaft Fitting",
    title: "SST Pure",
    slides: [
      {
        title: "What is SST Puring?",
        content:
          "SST (Shaft Straightening Technology) Puring is a process that identifies and marks the most stable orientation of a golf shaft. Every shaft has a 'spine' — a plane where it is most resistant to twisting. Puring aligns this plane consistently.",
        image: null,
      },
      {
        title: "Why Shafts Are Not All Equal",
        content:
          "Even precision-manufactured shafts have slight inconsistencies in wall thickness and material density. These create a 'hard' side and 'soft' side in each shaft. Without puring, these are oriented randomly, causing inconsistency club to club.",
        image: null,
      },
      {
        title: "The Puring Process",
        content:
          "Each shaft is placed in the SST Pure machine, which rotates it and measures deflection at multiple points. The machine identifies the most stable plane and marks it. The shaft is then installed with this mark aligned to a consistent position.",
        image: null,
      },
      {
        title: "Benefits for the Golfer",
        content:
          "Golfers with Pured shafts report tighter shot dispersion and more consistent feel club to club. The benefit is most noticeable in sets where every club feels the same — the result of eliminating random shaft inconsistencies.",
        image: null,
      },
      {
        title: "Recommending SST Pure",
        content:
          "Henry-Griffitts recommends SST Puring for all custom sets. The modest additional cost delivers measurable performance benefits. It is especially valuable for serious golfers who demand the most consistent equipment possible.",
        image: null,
      },
      {
        title: "Specifying SST Pure on the Order",
        content:
          "Check the SST Pure box on the fitting form to add this service. The build shop will pure each shaft before installation. The mark will be visible on the installed shaft — you can show the golfer the aligned marks as evidence of the work done.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What does SST Pure identify in each shaft?",
        options: ["Weight inconsistencies", "The most stable spine orientation", "Flex rating", "Kick point location"],
        answer: 1,
      },
      {
        q: "When should you recommend SST Pure?",
        options: ["Only for scratch golfers", "For all custom sets — the benefit is measurable", "Never — it's unnecessary", "Only for graphite shafts"],
        answer: 1,
      },
    ],
  },
  {
    id: 15,
    category: "Loft & Head Design",
    title: "Loft",
    slides: [
      {
        title: "What is Loft?",
        content:
          "Loft is the angle of the clubface relative to vertical. It determines how high the ball launches and how much backspin it generates. Lower loft = lower launch, less spin, more roll. Higher loft = higher launch, more spin, less roll.",
        image: null,
      },
      {
        title: "Loft Has Been Reduced Industry-Wide",
        content:
          "Modern 'game improvement' irons have significantly stronger (lower) lofts than clubs from 20 years ago. Today's 7-iron often has the loft of a traditional 5-iron. This makes clubs seem longer — but creates problems for many golfers.",
        image: null,
      },
      {
        title: "Too Little Loft Kills Swings",
        content:
          "When a golfer has insufficient loft, they cannot get the ball airborne properly with a normal swing. They begin to scoop — adding wrist flip to lift the ball. This compensation destroys consistency and creates a host of other swing problems.",
        image: null,
      },
      {
        title: "The HG Loft Philosophy",
        content:
          "Henry-Griffitts builds clubs with lofts that match the golfer's swing, not arbitrary marketing standards. If a golfer needs more loft to launch the ball correctly, they get it — even if the number on the club doesn't look as impressive.",
        image: null,
      },
      {
        title: "Fitting Loft to the Golfer",
        content:
          "Observe the golfer's ball flight with their current clubs. If they are hitting low shots that don't carry, they need more loft. If they are hitting high, ballooning shots, they may benefit from slightly less loft. Match loft to the golfer, not the golfer to a standard.",
        image: null,
      },
      {
        title: "Loft Gapping",
        content:
          "Proper club fitting includes verifying that the lofts create consistent distance gaps. Ideally, each club should go 10–15 yards farther than the next higher-lofted club. Overlapping distances indicate a gapping problem — often caused by inconsistent lofts.",
        image: null,
      },
      {
        title: "Wedge Lofts",
        content:
          "With irons having stronger lofts than a generation ago, wedge lofts must be carefully considered to maintain proper gapping. A modern set might go: PW (44°), Gap Wedge (50°), Sand Wedge (56°), Lob Wedge (60°). Ensure no big jumps.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What is one of the most destructive swing habits often caused by too little loft?",
        options: ["Slicing", "Hooking", "Scooping (flipping wrists)", "Casting"],
        answer: 2,
      },
      {
        q: "What is the ideal distance gap between consecutive irons?",
        options: ["5–8 yards", "10–15 yards", "20–25 yards", "30+ yards"],
        answer: 1,
      },
    ],
  },
  {
    id: 16,
    category: "Loft & Head Design",
    title: "Henry-Griffitts Heads",
    slides: [
      {
        title: "The HG Head Design Philosophy",
        content:
          "Every Henry-Griffitts head is designed to be adjustable for lie angle without compromising performance. This means softer metals in irons (carbon steel rather than stainless), allowing precise bending to match the golfer's needs.",
        image: null,
      },
      {
        title: "Cavity Back vs. Muscle Back",
        content:
          "**Cavity back irons** have weight distributed around the perimeter, creating a larger sweet spot and more forgiveness. **Muscle back (blade) irons** have weight concentrated behind the center, offering more feedback and workability. Match the head to the golfer's skill.",
        image: null,
      },
      {
        title: "The Sweet Spot and Fitting",
        content:
          "With proper fitting, the golfer hits the center of the face consistently — making the sweet spot location less critical. However, game improvement heads still benefit higher-handicap players who have more variation in their strike pattern.",
        image: null,
      },
      {
        title: "Material and Feel",
        content:
          "HG irons are available in carbon steel and stainless options. Carbon steel provides a softer feel and is easier to adjust for lie angle. Stainless is more durable but harder to bend. Most custom fits use carbon steel for maximum adjustability.",
        image: null,
      },
      {
        title: "Sole Width and Design",
        content:
          "Sole width affects how the club interacts with the turf. Wider soles help the club glide through thick rough and prevent digging. Narrower soles offer more versatility on tight lies. Match sole design to the courses and conditions the golfer plays.",
        image: null,
      },
      {
        title: "Current HG Iron Lines",
        content:
          "Refer to the current Henry-Griffitts catalog for available head styles. Options typically include: Player's cavity back (mid-handicap), Game improvement (higher handicap), and Player's blade (low handicap). Choose based on skill level and preference.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "Which material provides more feel feedback and is easier to bend for lie angle?",
        options: ["Stainless steel", "Carbon steel", "Titanium", "Tungsten"],
        answer: 1,
      },
    ],
  },
  {
    id: 17,
    category: "Club Fitting Fundamentals",
    title: "Weight & Swingweight",
    slides: [
      {
        title: "Total Weight vs. Swingweight",
        content:
          "**Total weight** is the actual weight of the club in grams. **Swingweight** is a measure of how heavy the club feels during the swing — it's the balance point relationship between head and grip. Two clubs can have the same total weight but different swingweights.",
        image: null,
      },
      {
        title: "The Swingweight Scale",
        content:
          "Swingweight is measured on a scale from A0 (lightest) to G10 (heaviest). Most men's clubs fall in the D0–D4 range. Most women's clubs fall in the C4–C8 range. Each letter represents a significant change in feel; each number is a subtle change.",
        image: null,
      },
      {
        title: "How Swingweight Affects Feel",
        content:
          "A club that feels too light (low swingweight) can make the golfer rush the swing and lose control. A club that feels too heavy (high swingweight) can slow the swing and cause fatigue. The right swingweight feels balanced and controllable throughout the swing.",
        image: null,
      },
      {
        title: "Setting Swingweight in Custom Clubs",
        content:
          "Henry-Griffitts builds to specific swingweights based on the golfer's strength, tempo, and preferences. Standard men's irons target D2. Adjustments are made by adding or removing weight in the head or grip end.",
        image: null,
      },
      {
        title: "The Length-Swingweight Relationship",
        content:
          "Every 1/2\" of club length changes swingweight by approximately 3 points. A club that is 1/2\" longer will feel 3 points heavier (e.g., D2 becomes D5). When adjusting length, swingweight must be recalibrated to maintain feel.",
        image: null,
      },
      {
        title: "Matching Swingweight Across the Set",
        content:
          "A matched-swingweight set feels consistent from club to club. This consistency allows the golfer to make the same swing with every iron. HG builds every club in the set to the same target swingweight.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "Adding 1/2\" of length to a club changes swingweight by approximately:",
        options: ["1 point", "3 points", "5 points", "No change"],
        answer: 1,
      },
      {
        q: "What is the typical swingweight target for men's irons?",
        options: ["C2", "D2", "E2", "F2"],
        answer: 1,
      },
    ],
  },
  {
    id: 18,
    category: "Club Fitting Fundamentals",
    title: "Grips",
    slides: [
      {
        title: "Why Grip Sizing Matters",
        content:
          "The grip is the golfer's only contact with the club. A grip that is too small causes excessive hand action and hooks. A grip that is too large restricts hand action and causes pushes/slices. Proper grip size allows neutral hand release.",
        image: null,
      },
      {
        title: "Measuring for Grip Size",
        content:
          "Measure the golfer's hand from the crease of the wrist to the tip of the middle finger. Cross-reference with the grip size chart: Under 7\" = Undersize. 7\"–7.5\" = Standard. 7.5\"–8\" = Midsize. Over 8\" = Oversize. Then verify with a grip test.",
        image: null,
      },
      {
        title: "Grip Materials",
        content:
          "**Rubber:** Durable, all-weather, most common. **Cord:** Better grip in wet conditions, firmer feel. **Wrap/Leather:** Softer feel, preferred by some traditionalists. Choose based on golfer preference and playing conditions.",
        image: null,
      },
      {
        title: "Grip Weight and Swingweight",
        content:
          "Heavier grips lower swingweight. Lighter grips raise swingweight. Standard grip is approximately 50 grams. If using a significantly lighter or heavier grip, swingweight must be recalculated.",
        image: null,
      },
      {
        title: "Completing the Grip Specification",
        content:
          "On the fitting form, document grip size (standard, midsize, etc.), grip model (if golfer has preference), and any build-up with tape if needed. A properly specified grip ensures the golfer can hold the club comfortably and release naturally.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "A hand measurement of 8\" corresponds to what grip size?",
        options: ["Undersize", "Standard", "Midsize", "Oversize"],
        answer: 3,
      },
      {
        q: "How do heavier grips affect swingweight?",
        options: ["Increase swingweight", "Decrease swingweight", "No effect", "Variable effect"],
        answer: 1,
      },
    ],
  },
  {
    id: 19,
    category: "Clubfitting Model",
    title: "The HG Clubfitting Model",
    slides: [
      {
        title: "The Complete Fitting Model Overview",
        content:
          "The Henry-Griffitts Clubfitting Model is a systematic approach that ensures every relevant specification is measured, tested, and fitted. It begins with evaluation, moves through lie board testing and length determination, and concludes with shaft, grip, and head selection. Every step builds on the last.",
        image: null,
      },
    ],
    quiz: [],
  },
  {
    id: 20,
    category: "Clubfitting Model",
    title: "Intro to the Fitting Process",
    slides: [
      {
        title: "Setting the Stage",
        content:
          "Before any fitting begins, the physical environment must be prepared. Ensure you have a clean, organized fitting area with adequate space for the golfer to swing. Have all tools ready: lie board, impact tape, fitting irons, fitting form, and measuring tape.",
        image: null,
      },
      {
        title: "The First Impression",
        content:
          "Introduce yourself and the Henry-Griffitts process. Explain that this fitting will be thorough and will take 45–60 minutes. Set the expectation that the golfer will leave understanding exactly why each specification was chosen for them.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "When should you introduce yourself and explain the HG process?",
        options: ["After the lie board test", "Before any fitting begins", "After the order is placed", "During delivery"],
        answer: 1,
      },
    ],
  },
  {
    id: 21,
    category: "Swing Analysis",
    title: "Swing Problems: Lie Angles",
    slides: [
      {
        title: "Clubs Too Flat: The Push/Fade Pattern",
        content:
          "When clubs are too flat, the heel rides up at impact, opening the face. The golfer sees pushes (straight right for right-handers) and fades that start right and curve more right. They may compensate by aiming left and strengthening grip — creating inconsistency.",
        image: null,
      },
      {
        title: "Clubs Too Upright: The Pull/Draw Pattern",
        content:
          "When clubs are too upright, the toe rides up at impact, closing the face. The golfer sees pulls (straight left) and draws that turn into hooks. They may compensate by aiming right and weakening grip — never quite trusting their swing.",
        image: null,
      },
      {
        title: "The Compensation Spiral",
        content:
          "Every compensation creates a new problem. Golfers playing wrong lie angles for years have developed complex compensations that feel 'natural' to them but are actually working against consistent ball-striking. Correct lie unlocks their real swing.",
        image: null,
      },
      {
        title: "Diagnosing Lie-Related Issues",
        content:
          "Ask the golfer: 'Do you consistently miss one direction?' A consistent miss pattern is the hallmark of lie angle error. Random misses are usually swing path issues, not equipment. The lie board confirms what the ball flight suggests.",
        image: null,
      },
      {
        title: "The Relief of Diagnosis",
        content:
          "Showing a golfer that their inconsistency is equipment-based, not swing-based, is profoundly relieving. They are not 'bad golfers' — they have been fighting equipment that didn't fit. Correct lie angle lets them trust their natural swing.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "Clubs that are too flat produce what ball flight pattern?",
        options: ["Pulls and hooks", "Pushes and fades", "High ballooning shots", "Low running shots"],
        answer: 1,
      },
    ],
  },
  {
    id: 22,
    category: "Swing Analysis",
    title: "Swing Problems: Club Length",
    slides: [
      {
        title: "Clubs Too Long: Crowding and Manipulation",
        content:
          "When clubs are too long, the golfer must stand too close to the ball or manipulate their swing to avoid hitting the ground early. This creates a cramped, uncomfortable stance and often results in heel strikes and pushes.",
        image: null,
      },
      {
        title: "Clubs Too Short: Reaching and Flat Plane",
        content:
          "When clubs are too short, the golfer reaches for the ball, flattening their swing plane. This causes toe strikes, a loss of power, and often a slice as the club cannot square in time. The golfer feels like they are swinging 'all arms.'",
        image: null,
      },
    ],
    quiz: [
      {
        q: "Clubs that are too long tend to cause contact at:",
        options: ["The toe", "The heel", "The center", "The top of the face"],
        answer: 1,
      },
    ],
  },
  {
    id: 23,
    category: "Swing Analysis",
    title: "Swing Problems: Shafts",
    slides: [
      {
        title: "Too Stiff: Loss of Feel and Distance",
        content:
          "A shaft too stiff for the golfer's swing cannot be loaded properly. The golfer loses feel for the clubhead, loses distance because the shaft doesn't release energy effectively, and tends to miss right with a weak fade.",
        image: null,
      },
      {
        title: "Too Soft: Timing Issues and Hooks",
        content:
          "A shaft too soft loads too much and releases too early. The golfer fights timing issues, often hooking the ball as the shaft over-releases. The club feels 'whippy' and uncontrollable, especially under pressure.",
        image: null,
      },
      {
        title: "Wrong Weight: Fatigue and Rhythm",
        content:
          "A shaft too heavy fatigues the golfer and slows their swing. A shaft too light feels 'loose' and can actually reduce control. The right weight allows the golfer to swing at their natural tempo without compensation.",
        image: null,
      },
      {
        title: "The Best Shaft Diagnosis Tool",
        content:
          "Watch the golfer's tempo, observe their ball flight tendencies, and ask how the club feels. If possible, test with shafts of different flexes and weights. The right shaft will feel 'effortless' — the wrong one always requires work.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "A shaft that is too stiff causes the golfer to:",
        options: ["Hook shots consistently", "Lose distance and miss right", "Top the ball", "Hit it too high"],
        answer: 1,
      },
    ],
  },
  {
    id: 24,
    category: "Swing Analysis",
    title: "Swing Problems: Loft",
    slides: [
      {
        title: "Too Little Loft: Scooping and Low Trajectory",
        content:
          "Golfers playing clubs with insufficient loft cannot get the ball airborne with a proper swing. They begin to scoop — adding wrist flip to lift the ball. This destroys compression and consistency. Low, running shots that don't carry are the result.",
        image: null,
      },
      {
        title: "Too Much Loft: Ballooning and Wind Sensitivity",
        content:
          "Too much loft produces high, ballooning shots that lose distance and are severely affected by wind. The golfer sees good-looking swings result in shots that go up but not forward. Reducing loft adds distance without changing the swing.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "Scooping (flipping the wrists) is most often caused by:",
        options: ["Clubs too upright", "Clubs too flat", "Clubs with too little loft", "Wrong grip size"],
        answer: 2,
      },
    ],
  },
  {
    id: 25,
    category: "Swing Analysis",
    title: "Too Little Loft Kills Swings!",
    slides: [
      {
        title: "The Industry's Dirty Secret",
        content:
          "Golf club manufacturers have been reducing lofts for decades to make clubs seem longer. Today's 7-iron has the loft of yesterday's 5-iron. This creates an epidemic of golfers who cannot get the ball in the air — and blame their swings.",
        image: null,
      },
      {
        title: "What Happens to the Swing",
        content:
          "When a golfer plays clubs with insufficient loft, they instinctively add loft at impact by flipping their wrists. This 'scooping' motion destroys all the good work done in the downswing. The club cannot be delivered with proper shaft lean.",
        image: null,
      },
      {
        title: "The Cascading Effect",
        content:
          "Scooping leads to: early extension (standing up through impact), loss of compression, inconsistent contact, and eventually back problems from the unnatural motion. One equipment error corrupts the entire swing.",
        image: null,
      },
      {
        title: "The HG Correction",
        content:
          "By fitting the golfer with clubs that have appropriate loft for their swing speed and attack angle, Henry-Griffitts eliminates the need to scoop. The golfer can swing naturally, deliver the club with forward shaft lean, and compress the ball correctly.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "Which of the following is NOT a problem caused by scooping (which is caused by too little loft)?",
        options: ["Early extension", "Loss of compression", "Improved distance control", "Inconsistent contact"],
        answer: 2,
      },
    ],
  },
  {
    id: 26,
    category: "Swing Analysis",
    title: "Swing Problems: Head Design",
    slides: [
      {
        title: "Wrong Head Design: Mismatched Forgiveness",
        content:
          "A scratch golfer playing game-improvement irons may find them too easy to work — they want feedback on mis-hits. A 20-handicapper playing blades will fight every shot. The head design must match the skill level and goals of the golfer.",
        image: null,
      },
      {
        title: "Sole Width and Turf Interaction",
        content:
          "A wide-sole club in the hands of a steep swinger will bounce and blade. A narrow-sole club for a sweeping swinger will dig. Watch how the golfer interacts with the turf and recommend sole width accordingly.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What type of head design is recommended for a 20-handicapper?",
        options: ["Blade irons", "Game improvement irons with cavity back", "Muscle back irons", "Whatever looks best"],
        answer: 1,
      },
    ],
  },
  {
    id: 27,
    category: "Swing Analysis",
    title: "Swing Analysis: Supplemental",
    slides: [
      {
        title: "Putting It All Together",
        content:
          "Every swing problem discussed in this section has an equipment solution. The Henry-Griffitts fitter's job is to identify which problems are equipment-based and which are truly swing-based. Equipment fixes are immediate and lasting; swing fixes take time and practice.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What is the Henry-Griffitts fitter's primary role?",
        options: ["Give swing lessons", "Sell the most expensive clubs", "Identify equipment solutions to swing problems", "Recommend practice drills"],
        answer: 2,
      },
    ],
  },
  {
    id: 28,
    category: "Sales & Customer Skills",
    title: "Learning Styles",
    slides: [
      {
        title: "Why Learning Styles Matter in Fitting",
        content:
          "Golfers learn and make decisions differently. Some need to see data. Some need to feel the difference. Some need to hear the logic. Adapting your communication to the golfer's style builds trust and makes the fitting experience more effective.",
        image: null,
      },
      {
        title: "The Visual Learner",
        content:
          "Show them everything. Put the lie board marks in front of them. Use diagrams to explain lie angle. Show before/after ball flight if possible. Visual learners believe what they can see — give them evidence.",
        image: null,
      },
      {
        title: "The Kinesthetic Learner",
        content:
          "Let them feel the difference. Have them hit shots with the fitted club vs. their current club. Let them hold clubs of different weights and lengths. Kinesthetic learners believe what they can feel — let them experience it.",
        image: null,
      },
      {
        title: "The Analytical Learner",
        content:
          "Give them data and logic. Explain the 1-degree rule. Show the fit chart calculations. Analytical learners want to understand WHY — give them the reasoning behind every recommendation.",
        image: null,
      },
      {
        title: "The Social Learner",
        content:
          "These golfers are influenced by the experiences of others. Share stories of other golfers who benefited from fitting. Mention other customers who had similar results. Social learners want to know they're not alone.",
        image: null,
      },
      {
        title: "The Skeptic",
        content:
          "Skeptics are your best potential advocates. Don't fight their skepticism — embrace it. Say: 'I don't expect you to believe me. Let's let the lie board decide.' When the evidence convinces them, they become fierce believers.",
        image: null,
      },
      {
        title: "Reading the Room",
        content:
          "You will often not know a golfer's learning style upfront. Start with visual evidence (lie board marks), offer to let them feel the difference, and be prepared to explain the logic. Watch what they respond to and adapt.",
        image: null,
      },
      {
        title: "Adapting Your Language",
        content:
          "Avoid jargon with new golfers. Use plain language: 'Your clubs are too flat, which is why the ball goes right.' With experienced golfers, you can use technical terms. Match your vocabulary to their knowledge level.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "A golfer who responds best to seeing lie board marks and face tape evidence is likely a:",
        options: ["Kinesthetic learner", "Visual learner", "Analytical learner", "Social learner"],
        answer: 1,
      },
      {
        q: "How should you handle a skeptical golfer?",
        options: ["Argue with evidence until they agree", "Let the lie board and results speak for themselves", "Give them a discount", "Avoid technical details"],
        answer: 1,
      },
    ],
  },
  {
    id: 29,
    category: "Sales & Customer Skills",
    title: "Helpful Tips to Sell",
    slides: [
      {
        title: "Selling the Value, Not the Price",
        content:
          "Custom-fitted clubs are an investment in consistent golf. Focus on what the golfer gains: fewer lost balls, more greens in regulation, lower scores, more enjoyment. The cost per round over the life of the clubs is minimal compared to the benefit.",
        image: null,
      },
      {
        title: "The ROI Conversation",
        content:
          "Help the golfer calculate their investment return. If they play 50 rounds per year and the clubs last 7 years, that's 350 rounds. A $2,000 set costs less than $6 per round. That's less than a sleeve of balls — for clubs that actually fit.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What should the sales conversation focus on instead of price?",
        options: ["Discounts available", "The value and benefits to the golfer's game", "Competitor pricing", "Manufacturing costs"],
        answer: 1,
      },
    ],
  },
  {
    id: 30,
    category: "Operations",
    title: "Completing the Fitting Form",
    slides: [
      {
        title: "The Fitting Form: Your Precision Tool",
        content:
          "The Henry-Griffitts Fitting Form is designed to capture every specification accurately. A complete and legible form ensures the build shop produces exactly what was fitted. Take time to complete it thoroughly.",
        image: null,
      },
      {
        title: "Customer Information Section",
        content:
          "Complete full name, contact phone, email, and address. Verify spelling with the customer — their name will appear on the certificate and any future orders. Accurate contact information ensures smooth delivery coordination.",
        image: null,
      },
      {
        title: "Measurement Section",
        content:
          "Record: wrist-to-floor, hand size, height, and estimated swing speed. Include tempo observation (fast, medium, smooth). These measurements form the baseline for all specifications.",
        image: null,
      },
      {
        title: "Specification Section",
        content:
          "For each club in the set, record: lie angle correction, length adjustment, shaft specification, grip size and model, and head model. Be specific — 'TT DG S300' is better than 'stiff steel.'",
        image: null,
      },
      {
        title: "Review Before Submission",
        content:
          "Before the customer leaves: read back every specification aloud and confirm. Check that handwriting is legible. Ensure the customer signature acknowledges the order. A mistake caught now is easy to fix; a mistake in the build shop is costly.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What should you do before the customer leaves after completing the fitting form?",
        options: ["Take payment only", "Read back all specifications and confirm", "Schedule delivery", "Send them home immediately"],
        answer: 1,
      },
    ],
  },
  {
    id: 31,
    category: "Operations",
    title: "Ordering Clubs",
    slides: [
      {
        title: "Submitting Your Order",
        content:
          "Henry-Griffitts orders are submitted through the dealer portal or via the fitting form sent to the build shop. Ensure all fields are complete. Incomplete orders cause delays — the build shop will hold the order until clarified.",
        image: null,
      },
      {
        title: "Order Verification",
        content:
          "The system will flag any unusual specifications (extreme lie angles, uncommon lengths). If flagged, verify your measurements and confirm the specification is intentional. This quality check catches errors before they become mistakes.",
        image: null,
      },
      {
        title: "Lead Times",
        content:
          "Standard lead time for custom HG sets is 3–4 weeks. Complex orders or special shaft requests may take longer. Always set customer expectations at the time of order — under-promise and over-deliver.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What is the standard lead time for a custom Henry-Griffitts set?",
        options: ["1 week", "3–4 weeks", "6–8 weeks", "Same day"],
        answer: 1,
      },
    ],
  },
  {
    id: 32,
    category: "Operations",
    title: "Delivery of Equipment",
    slides: [
      {
        title: "Receiving the Order",
        content:
          "When the set arrives, inspect every club before contacting the customer. Verify lie angles, lengths, grips, and shafts match the order. Check for cosmetic damage. Address any discrepancies with HG before the customer sees the clubs.",
        image: null,
      },
      {
        title: "The Delivery Appointment",
        content:
          "Schedule a delivery appointment with the customer. This is not just a handoff — it's the completion of the fitting experience. Plan 30 minutes to review the clubs, explain each specification, and let them hit a few shots.",
        image: null,
      },
      {
        title: "The First-Hit Moment",
        content:
          "The moment a correctly fitted golfer hits their new clubs is magical. They feel the difference immediately. This is the moment that creates referrals and repeat business. Make it special — don't just hand them a box.",
        image: null,
      },
      {
        title: "Handling Discrepancies",
        content:
          "If any specification does not match the order, contact HG immediately. Do not attempt field corrections on a new set — the warranty covers build errors. Document any issues and coordinate replacement through proper channels.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What should you do when the clubs arrive before contacting the customer?",
        options: ["Call them immediately", "Inspect every club against the order", "Ship directly to customer", "Put in storage"],
        answer: 1,
      },
    ],
  },
  {
    id: 33,
    category: "Operations",
    title: "Care of the ICS System and Lie Board",
    slides: [
      {
        title: "Lie Board Maintenance",
        content:
          "The lie board is a precision instrument. Store flat in a dry location. Clean after each use. Replace the board surface if it shows wear grooves. Re-verify against a flat surface periodically. A warped board gives false readings.",
        image: null,
      },
      {
        title: "Fitting Club Maintenance",
        content:
          "Keep your fitting irons clean and the grooves sharp. Verify the lie angles on your fitting clubs annually — they can shift over time. A fitting iron with incorrect lie angle leads to incorrect recommendations.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "How often should you re-verify your fitting clubs' lie angles?",
        options: ["Never — they are factory set", "Annually", "After every fitting", "Every 5 years"],
        answer: 1,
      },
    ],
  },
  {
    id: 34,
    category: "Operations",
    title: "Organizing a File System",
    slides: [
      {
        title: "Why Customer Records Matter",
        content:
          "A comprehensive customer file enables follow-up service, reorders, and referral tracking. Every customer who orders once may order again — for themselves, for family members, as gifts. The file is your business history.",
        image: null,
      },
      {
        title: "What to Keep on File",
        content:
          "For each customer, retain: the signed fitting form, order confirmation, delivery receipt, and any notes about preferences or feedback. Digital copies are acceptable — ensure backup.",
        image: null,
      },
      {
        title: "Physical vs. Digital Records",
        content:
          "Digital records are preferred for searchability and backup. If using paper, organize alphabetically by last name with annual archives. Whatever system you use, be consistent and thorough.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What is the recommended format for customer records?",
        options: ["Paper only", "Digital with backup", "Memory only", "No records needed"],
        answer: 1,
      },
    ],
  },
  {
    id: 35,
    category: "Sales & Customer Skills",
    title: "Customer Follow-Up",
    slides: [
      {
        title: "Why Follow-Up is Critical",
        content:
          "The fitting experience continues after delivery. Follow-up demonstrates that you care about results, not just the sale. It builds loyalty and generates referrals — satisfied customers tell their golf friends.",
        image: null,
      },
      {
        title: "The 2-Week Check-In",
        content:
          "Call or email the golfer approximately 2 weeks after delivery. Ask: 'How are the clubs performing? Any questions?' Most will rave. If there are issues, address them promptly — early intervention prevents larger problems.",
        image: null,
      },
      {
        title: "The 30-Day Follow-Up",
        content:
          "At 30 days, the golfer has had time to play multiple rounds. Ask for specific feedback: 'Are you hitting more greens? Is the ball flight what you expected?' This is also the perfect time to ask for referrals.",
        image: null,
      },
      {
        title: "Long-Term Relationship Building",
        content:
          "Re-fit every customer every 3–5 years as their game changes. Send a note at the anniversary of their purchase. When they're ready for new woods or wedges, you'll be the first person they call.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "When is the best time to ask for a referral?",
        options: ["At the fitting", "At delivery", "At the 30-day follow-up when they've experienced results", "Never"],
        answer: 2,
      },
    ],
  },
  {
    id: 36,
    category: "Company Overview",
    title: "HG Commitment to You and Your Customers",
    slides: [
      {
        title: "What Henry-Griffitts Promises You",
        content:
          "As an authorized HG fitter, you have access to: comprehensive training, ongoing support, premium fitting tools, and a product that genuinely delivers. HG succeeds when you succeed — your success is the foundation of our business.",
        image: null,
      },
      {
        title: "What Henry-Griffitts Promises Your Customers",
        content:
          "Every customer receives clubs built exactly to specification, quality-checked before shipping, and backed by HG's warranty. They get equipment that fits their swing — not a compromise, not an approximation, but precision.",
        image: null,
      },
      {
        title: "You Are the Brand",
        content:
          "Henry-Griffitts' reputation exists in the hands of our authorized fitters. Your professionalism, your thoroughness, and your commitment to the golfer's experience defines what HG means in your market. You are the brand.",
        image: null,
      },
      {
        title: "Certification Complete - What's Next",
        content:
          "Congratulations on completing the Henry-Griffitts Certification Program. You now have the knowledge to fit any golfer with precision and confidence. Apply what you've learned, continue asking questions, and welcome to the HG family.",
        image: null,
      },
    ],
    quiz: [
      {
        q: "What does Henry-Griffitts guarantee for every set of clubs built?",
        options: ["Lowest price in the market", "Built exactly to specification and quality-checked", "Fastest delivery", "Free re-fitting forever"],
        answer: 1,
      },
    ],
  },
];

export const CATEGORIES = [...new Set(MODULES.map(m => m.category))];
export const PASS_SCORE = 80;
